/**
 * Admin Setup → Legal & Policies → Document Acknowledgement Message.
 *
 * The wording a supplier sees when a document asks to be acknowledged — the
 * Read & Accept banner and the Read & Understood confirmation. Blank keeps the
 * standard wording; a customer can replace it, e.g. to state that all
 * documents and drawings are its intellectual property under its terms and
 * conditions. Saved as this company's Admin Setup override.
 */
import { useCallback, useEffect, useState } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { AlertCircle, Loader2, Save, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../utils/errorUtils';

const MAX_LENGTH = 2000;
const STANDARD_WORDING = 'Open it, review it, then click Read & Accept below.';

export function DocumentAcknowledgementCard() {
  const [message, setMessage] = useState('');
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await platformSettingsAPI.getSettings();
      const value = res.data.document_acknowledgement_message || '';
      setMessage(value);
      setSaved(value);
    } catch (err: unknown) {
      setLoadError(getApiErrorMessage(err, 'Failed to load the acknowledgement message'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (value: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      await platformSettingsAPI.patchSettings({ document_acknowledgement_message: value });
      setMessage(value);
      setSaved(value);
      toast.success(value ? 'Acknowledgement message saved' : 'Standard wording restored');
    } catch (err: unknown) {
      const reason = getApiErrorMessage(err, 'Failed to save the acknowledgement message');
      setSaveError(reason);
      toast.error(reason);
    } finally {
      setSaving(false);
    }
  };

  const trimmed = message.trim();
  const dirty = trimmed !== saved.trim();
  const tooLong = trimmed.length > MAX_LENGTH;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Document Acknowledgement Message
        </CardTitle>
        <CardDescription>
          Shown to suppliers when a document asks them to acknowledge it (Read &amp; Accept and
          Read &amp; Understood). Leave blank to use the standard wording.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : loadError ? (
          <div className="flex items-center gap-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4" /> {loadError}
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="document-acknowledgement-message">Message</Label>
              <Textarea
                id="document-acknowledgement-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder={`Standard wording: "${STANDARD_WORDING}"`}
              />
              <p className={`text-xs ${tooLong ? 'text-red-600' : 'text-muted-foreground'}`}>
                {trimmed.length} / {MAX_LENGTH} characters · plain text, line breaks are kept
              </p>
            </div>
            {saveError && (
              <div className="flex items-center gap-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4" /> {saveError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              {saved && (
                <Button variant="outline" onClick={() => save('')} disabled={saving}>
                  Use standard wording
                </Button>
              )}
              <Button onClick={() => save(trimmed)} disabled={saving || !dirty || tooLong}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Message
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
